// Smart video preloader: keep ~2 mobile videos warm for the CURRENT state's category,
// evict the previous state's buffers on transition, and rotate through a category's
// videos in a per-visit random order. Uses the manifest's mobileUrl (falling back to url).
import type { ChatFile } from './types';

export type VideoCategory = 'wait' | 'listening' | 'processing' | 'eager' | 'error';

const BUFFER_PER_CATEGORY = 2;

export class VideoPreloader {
  private byCat = new Map<VideoCategory, string[]>();
  private warm = new Map<VideoCategory, HTMLVideoElement[]>();
  private rotation = new Map<VideoCategory, number>();
  private container: HTMLDivElement;

  constructor() {
    this.container = document.createElement('div');
    this.container.setAttribute('aria-hidden', 'true');
    this.container.style.cssText =
      'position:absolute;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none';
    document.body.appendChild(this.container);
  }

  setFiles(files: ChatFile[]): void {
    this.byCat.clear();
    for (const f of files) {
      if (f.videoType !== 'statusVideo' || !f.category) continue;
      const url = f.mobileUrl || f.url;
      if (!url) continue;
      const cat = f.category as VideoCategory;
      if (!this.byCat.has(cat)) this.byCat.set(cat, []);
      this.byCat.get(cat)!.push(url);
    }
    // Random play order per page load (Fisher-Yates), so a visit never starts on the
    // same video and the rotation below walks the whole category before repeating.
    for (const urls of this.byCat.values()) {
      for (let i = urls.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [urls[i], urls[j]] = [urls[j], urls[i]];
      }
    }
    this.rotation.clear();
  }

  has(cat: VideoCategory): boolean {
    return (this.byCat.get(cat)?.length ?? 0) > 0;
  }

  count(cat: VideoCategory): number {
    return this.byCat.get(cat)?.length ?? 0;
  }

  private release(els: HTMLVideoElement[]): void {
    els.forEach((e) => {
      e.removeAttribute('src');
      e.load();
      e.remove();
    });
  }

  /** Drop every warm buffer (page backgrounded); preload() rebuilds them on demand. */
  clear(): void {
    for (const els of this.warm.values()) this.release(els);
    this.warm.clear();
  }

  /** Warm the next BUFFER_PER_CATEGORY videos for `cat`, evicting everything else. */
  preload(cat: VideoCategory): void {
    for (const [c, els] of this.warm) {
      if (c !== cat) {
        this.release(els);
        this.warm.delete(c);
      }
    }
    const urls = this.byCat.get(cat) ?? [];
    if (!urls.length) return;
    const want = Math.min(BUFFER_PER_CATEGORY, urls.length);
    const start = this.rotation.get(cat) ?? 0;
    const wanted: string[] = [];
    for (let i = 0; i < want; i++) wanted.push(urls[(start + i) % urls.length]);
    // Keep what is still upcoming, drop what has already been played.
    const old = this.warm.get(cat) ?? [];
    const have = old.filter((e) => wanted.includes(e.getAttribute('src') ?? ''));
    this.release(old.filter((e) => !have.includes(e)));
    for (const url of wanted) {
      if (have.some((e) => e.getAttribute('src') === url)) continue;
      const v = document.createElement('video');
      v.preload = 'auto';
      v.muted = true;
      v.src = url;
      v.load();
      this.container.appendChild(v);
      have.push(v);
    }
    this.warm.set(cat, have);
  }

  /** A url for `cat`, rotating through the available videos for variety. */
  pick(cat: VideoCategory): string | null {
    const urls = this.byCat.get(cat) ?? [];
    if (!urls.length) return null;
    const idx = (this.rotation.get(cat) ?? 0) % urls.length;
    this.rotation.set(cat, idx + 1);
    return urls[idx];
  }
}
